/**
 * Who is allowed to see the admin UI.
 *
 * IMPORTANT: this is a UI gate, not a security boundary. It decides which tabs
 * and buttons render. Anyone can edit JavaScript in devtools and flip the flag,
 * after which their requests still go to Supabase with the anon key. What those
 * requests are actually permitted to do is decided entirely by Row Level
 * Security policies on the server. Treat RLS as the real control.
 *
 * What this gate does fix: admin used to be granted from
 * getCurrentUser()?.email alone, and "Quick Join" let anyone type any address
 * with no verification whatsoever - so typing the admin's address was enough to
 * be handed the admin UI. The only guard read `known_auth_emails` out of
 * localStorage, which is empty in any fresh browser.
 *
 * Requiring a real Supabase session closes that: a session can only be obtained
 * by proving control of the address (a password that was confirmed by email, an
 * emailed one-time code, or Google). Note that Supabase refuses to issue a
 * session to an unconfirmed password account while mailer_autoconfirm is off,
 * so holding a session already implies the address was verified.
 */

export const ADMIN_EMAILS = [
  'jaygodfrey1@gmail.com'
];

/**
 * @param {object}  args
 * @param {boolean} args.hasVerifiedSession true only for a real Supabase auth
 *   session - NOT for email-only "Quick Join" users
 * @param {object|null} args.currentUser the shape returned by getCurrentUser()
 * @returns {boolean}
 */
export const isAdminUser = ({ hasVerifiedSession, currentUser }) => {
  if (!hasVerifiedSession) return false;
  if (!currentUser) return false;

  // Defence in depth: an email-only identity must never qualify, even if some
  // future change lets one coexist with a session.
  if (currentUser.isEmailOnly) return false;

  const email = currentUser.email?.toLowerCase?.();
  if (!email) return false;

  return ADMIN_EMAILS.some((adminEmail) => adminEmail.toLowerCase() === email);
};

export default isAdminUser;
